import { Animal, Customer } from 'src/app/api/models';
import { AppointmentEditPayload } from 'src/app/calendar/containers/appointment-modify/appointment-edit-payload.model';
import { AppointmentModifyDetails } from 'src/app/calendar/shared/models/appointment-modify-details.model';
import { EncoderHelper } from './encoder.helper';

export class AppointmentHelper {
  /**
   * Extracts the customer's name, using Appointment.remarkCustomer as fallback
   * in case firstName and lastName fields are not available.
   */
  static extractCustomerName(remarkCustomer?: string, customer?: Customer) {

    if (customer?.lastName || customer?.firstName) {
      return `${customer?.lastName ?? ''}${(customer?.lastName && customer?.firstName) ? ',' : ''} ${customer?.firstName ?? ''}`;
    }

    return remarkCustomer ?? '';
  }

  /**
   * Extracts the customer's fullname, with the firstName shortened
   * in the form: "Müller T."
   */
  static extractCustomerNameShorten(remarkCustomer?: string, customer?: Customer) {
    if (customer?.lastName || customer?.firstName) {
      return `${customer.lastName ?? ''} ${customer.firstName?.charAt(0)}.`;
    }

    return remarkCustomer?.replace(',', '') ?? '';
  }

  static hasValidAppointmentAnimals(animals: Array<Animal> | undefined): boolean {
    if (Array.isArray(animals)) {
      const hasValidAnimal = animals.filter((animal) => animal.animalId);
      return (hasValidAnimal.length > 0);
    }
    return false;
  }

  static sortAnimalsAsc(uniqueAnimalsObj: Record<string, Animal>): Animal[] {
    let uniqueAnimals = Object.values(uniqueAnimalsObj);
    // animals are sorted by sort value and then
    // animals with same sort are sorted alphabetically
    uniqueAnimals.sort((a: Animal, b: Animal) => {
      if (a.sort && b.sort) {
        return a.sort - b.sort;
      } else if (a.sort) {
        return a.sort;
      }
      return b.sort ?? 0;
    });
    let sortAnimalObj: Record<number, Animal[]> = {};
    uniqueAnimals.forEach(a => {
      let key = a.sort;
      if (!key) {
        key = 99999;
      }
      if (sortAnimalObj[key]) {
        sortAnimalObj[key] = [a, ...sortAnimalObj[key]];
      } else {
        sortAnimalObj[key] = [a];
      }
    });
    uniqueAnimals = [];
    for (const value of Object.values(sortAnimalObj)) {
      value.sort((a, b) => (a.name ?? '').trim().localeCompare((b.name ?? '').trim()));
      uniqueAnimals = [...uniqueAnimals, ...value];
    }
    return uniqueAnimals;
  }

  /**
   * Formats the Appointment DTO into the Command format for the backend.
   * @param originalData The existing `Appointment` object
   */
  static formatOriginalAppointment(originalData: AppointmentModifyDetails): AppointmentEditPayload {
    return {
      bookId: originalData.bookId,
      animals: originalData.animals,
      end: originalData.end,
      start: originalData.start,
      stateIds: originalData.states?.map(s => s.appStateId ?? '') ?? [],
      resourceIds: originalData.resourceIds,
      reasonIds: originalData.reasonIds,
      customerId: originalData.customer?.id,
      flags: originalData?.flags,
      referralFromVetName: originalData?.transferringVet,
      referralFromVetId: originalData?.transferringVetId,
      remarkCustomer: originalData.remarkCustomer ? EncoderHelper.encode(originalData.remarkCustomer): '',
      remarkAnimal: originalData.remarkAnimal ? EncoderHelper.encode(originalData.remarkAnimal): '',
      remarkAppointment: originalData.remarkAppointment ? EncoderHelper.encode(originalData.remarkAppointment): '',
      shouldSendNotification: originalData.shouldSendNotification,
      remarkReason: originalData.remarkReason ? EncoderHelper.encode(originalData.remarkReason): '',
      remarkAddress: originalData.remarkAddress ? EncoderHelper.encode(originalData.remarkAddress): '',
      startingAt: originalData.startingAt,
      endingAt: originalData.endingAt,
      timeRange: originalData.timeRange,
      timeRangeId: originalData.timeRangeId
    } as AppointmentEditPayload;
  }
}
